import { StyleSheet, Font } from "@react-pdf/renderer";

Font.register({
    family: "BrittiSans",
    fonts: [
        { src: "/fonts/BrittiSans-400-tnum.otf", fontWeight: 400 },
        { src: "/fonts/BrittiSans-600-tnum.otf", fontWeight: 600 },
        { src: "/fonts/BrittiSans-700-tnum.otf", fontWeight: 700 },
    ]
});

// Variant design tokens
const G = {
    900: '#111111',
    800: '#272727',
    700: '#434343',
    600: '#636363',
    400: '#a0a0a0',
    200: '#cdcdcd',
    100: '#e1e1e1',
    50:  '#f4f4f4',
    25:  '#fafafa',
    white: '#ffffff',
};

const BORDER_WIDTH = 1;
const BORDER_STYLE = 'solid';
const BORDER_COLOR = G[200];

const cellBorder = {
    borderBottomWidth: BORDER_WIDTH,
    borderBottomStyle: BORDER_STYLE,
    borderBottomColor: BORDER_COLOR,
    borderLeftWidth: BORDER_WIDTH,
    borderLeftStyle: BORDER_STYLE,
    borderLeftColor: BORDER_COLOR,
};

const sharedStyles = StyleSheet.create({
    page: {
        backgroundColor: G.white,
        fontFamily: 'BrittiSans',
        paddingTop: 36,
        paddingBottom: 36,
        paddingLeft: 24,
        paddingRight: 24,
    },

    // ── Header ───────────────────────────────────────────────────────────────
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 24,
        paddingLeft: 4,
    },
    headerLeft: {
        flex: 1,
    },
    headerRight: {
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
    },

    // ── Typography ───────────────────────────────────────────────────────────
    h1: {
        fontSize: 40,
        fontWeight: 600,
        color: G[900],
        marginBottom: 48,
        fontVariantNumeric: 'lining-nums tabular-nums',
        fontFeatureSettings: 'lnum, tnum', 
    },
    metaRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: 4,
    },
    metaLabel: {
        fontSize: 10,
        fontWeight: 400,
        color: G[900],
        width: 48,
    },
    metaValue: {
        fontSize: 10,
        fontWeight: 600,
        color: G[900],
    },

    // ── Table ────────────────────────────────────────────────────────────────
    tableRow: {
        flexDirection: 'row',
        paddingLeft: 4,
    },

    // ── Hours cell (View + Text) ─────────────────────────────────────────────
    // View: fixed width, borders, flex alignment
    hours_cell: {
        width: 22,
        minHeight: 22,
        justifyContent: 'flex-end',  // bottom-align content
        alignItems: 'flex-end',      // right-align content
        paddingRight: 4,
        paddingLeft: 2,
        paddingBottom: 4,
        ...cellBorder,
    },
    // Text: font size and color only
    hours_text: {
        fontSize: 7,
        color: G[900],
        fontWeight: 400,
        fontVariantNumeric: 'lining-nums tabular-nums',
    },

    // ── Name cell (View + Text) ──────────────────────────────────────────────
    name_cell_header: {
        width: 80,
        justifyContent: 'flex-end',  // bottom-align
        paddingBottom: 4,
        paddingTop: 4,
        fontWeight: 600,
    },
    name_cell: {
        width: 80,
        justifyContent: 'flex-end',  // bottom-align
        paddingBottom: 4,
        paddingTop: 4,
        fontWeight: 400,
        borderBottomWidth: BORDER_WIDTH,
        borderBottomStyle: BORDER_STYLE,
        borderBottomColor: BORDER_COLOR,
    },
    name_text: {
        fontSize: 7,
        color: G[900],
    },

    // ── Sum cell (View + Text) ───────────────────────────────────────────────
    sum_cell_header: {
        width: 25,
        justifyContent: 'flex-end',  // bottom-align
        alignItems: 'flex-end',      // right-align
        paddingRight: 0,
        paddingBottom: 4,
        borderLeftWidth: BORDER_WIDTH,
        borderLeftStyle: BORDER_STYLE,
        borderLeftColor: BORDER_COLOR,
    },
    sum_cell: {
        width: 25,
        justifyContent: 'flex-end',  // bottom-align
        alignItems: 'flex-end',      // right-align
        paddingRight: 0,
        paddingBottom: 4,
        ...cellBorder,
    },
    sum_text: {
        fontSize: 7,
        color: G[900],
        fontWeight: 600,
        fontVariantNumeric: 'lining-nums tabular-nums',
    },

    // ── Heading row modifier ─────────────────────────────────────────────────
    heading_row: {
        backgroundColor: G[50],
        borderRadius: 4,
        height: 17,
        paddingLeft: 4,
    },
    heading_cell_days: {
        width: 22,
        justifyContent: 'flex-end',  // bottom-align content
        alignItems: 'flex-end',      // right-align content
        paddingRight: 4,
        paddingBottom: 4,
        borderLeftWidth: BORDER_WIDTH,
        borderLeftStyle: BORDER_STYLE,
        borderLeftColor: BORDER_COLOR,
    }, 
    heading_cell: {
        width: 22,
        minHeight: 22,
        justifyContent: 'flex-end',  // bottom-align content
        alignItems: 'flex-end',      // right-align content
        paddingRight: 4,
        paddingBottom: 4,
    },
    heading_text: {
        fontSize: 7,
        fontWeight: 600,
        color: G[900],
    },
    weekend_text: {
        color: '#fe6d6d', // coral/500
    },

    // ── Footer / totals row modifier ─────────────────────────────────────────
    footer_row: {

    },
    footer_text: {
        fontSize: 7,
        fontWeight: 600,
        color: G[900],
        fontVariantNumeric: 'lining-nums tabular-nums',
    },

    // ── Total amount ─────────────────────────────────────────────────────────
    amount_row: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'baseline',
        marginTop: 24,
        gap: 8,
        paddingRight: 3,
    },
    amount_label: {
        fontSize: 10,
        fontWeight: 400,
        color: G[900],
    },
    amount_value: {
        fontSize: 10,
        fontWeight: 600,
        color: G[900],
    },

    // ── Page footer ──────────────────────────────────────────────────────────
    pageFooter: {
        position: 'absolute',
        bottom: 24,
        left: 28,
        right: 26,
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: 6,
    },
    pageFooterText: {
        fontSize: 7,
        color: G[900],
        flex: 1,
    },
});

export default sharedStyles;
